// ============================================================
// Audit trail client (Brief v2 · Task 6).
//
// Every consequential action — inspection submit, NCR raise, RFI create,
// document approve, member invite — is appended to public.audit_log via the
// log_audit RPC, stamped with the acting user + server time. The QC-manager
// audit viewer reads them back for the handover pack / compliance export.
//
// Logging is best-effort and never blocks the UI: a failed append is swallowed
// (offline), the action itself still completes locally.
// ============================================================
import { supabase } from "./supabase";
import { getProjectId } from "./persistence";
import { eng } from "./engineApi";

function actorName(): string {
  try {
    const e = eng();
    const r = e.D.roles[e.currentRole as keyof typeof e.D.roles] as { operator?: string } | undefined;
    return r?.operator || "Unknown";
  } catch {
    return "Unknown";
  }
}

export interface AuditEntry {
  id: number;
  actor_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  new_value: unknown;
  created_at: string;
}

/** Append one audit entry for the active project. Best-effort. */
export async function logAudit(
  action: string,
  entityType: string,
  entityId: string,
  newValue?: unknown,
): Promise<void> {
  if (!supabase) return;
  try {
    await supabase.rpc("log_audit", {
      p_project: getProjectId(),
      p_action: action,
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_actor_name: actorName(),
      p_new_value: newValue ?? null,
    });
  } catch {
    /* offline — action already applied locally */
  }
}

/** Read the audit trail for the active project, newest first. */
export async function fetchAuditLog(limit = 500): Promise<AuditEntry[]> {
  if (!supabase) return [];
  try {
    const { data, error } = await supabase
      .from("audit_log")
      .select("id, actor_name, action, entity_type, entity_id, new_value, created_at")
      .eq("project_id", getProjectId())
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) return [];
    return (data || []) as AuditEntry[];
  } catch {
    return [];
  }
}
